
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuthors } from '@/hooks/useAuthors';
import { useCreateBook } from '@/hooks/useCreateBook';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import BookFormatForm from './BookFormatForm';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  authorId: z.string().min(1, 'Author selection is required'),
});

interface BookFormat {
  format: string;
  isbn: string;
  publishDate: string;
}

interface AddBookFormProps {
  onSuccess?: () => void;
}

const AddBookForm: React.FC<AddBookFormProps> = ({ onSuccess }) => {
  const { data: authors, isLoading: isLoadingAuthors } = useAuthors();
  const createBook = useCreateBook();
  const { toast } = useToast();
  
  const [formats, setFormats] = useState<BookFormat[]>([{
    format: 'paperback',
    isbn: '',
    publishDate: format(new Date(), 'yyyy-MM-dd')
  }]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      authorId: '',
    },
  });

  const validateFormats = (): boolean => {
    // Check for empty ISBNs
    if (formats.some(f => !f.isbn.trim())) {
      toast({
        title: 'Validation Error',
        description: 'All formats must have an ISBN.',
        variant: 'destructive',
      });
      return false;
    }

    // Check for duplicate formats
    const formatTypes = formats.map(f => f.format);
    const uniqueFormats = new Set(formatTypes);
    if (formatTypes.length !== uniqueFormats.size) {
      toast({
        title: 'Validation Error',
        description: 'Duplicate book formats are not allowed.',
        variant: 'destructive',
      });
      return false;
    }

    // Check for duplicate ISBNs
    const isbns = formats.map(f => f.isbn.trim());
    const uniqueIsbns = new Set(isbns);
    if (isbns.length !== uniqueIsbns.size) {
      toast({
        title: 'Validation Error',
        description: 'Duplicate ISBNs are not allowed.',
        variant: 'destructive',
      });
      return false;
    }

    return true;
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!validateFormats()) {
      return;
    }

    try {
      // Create a book for each format
      for (const bookFormat of formats) {
        await createBook.mutateAsync({
          title: values.title,
          isbn: bookFormat.isbn,
          publishDate: bookFormat.publishDate,
          authorId: values.authorId,
          format: bookFormat.format,
        });
      }
      
      if (onSuccess) onSuccess();
      form.reset();
      setFormats([{
        format: 'paperback',
        isbn: '',
        publishDate: format(new Date(), 'yyyy-MM-dd')
      }]);
    } catch (error) {
      console.error('Error creating book:', error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Book Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter book title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="authorId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Author</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an author" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {isLoadingAuthors ? (
                    <SelectItem value="loading" disabled>
                      Loading authors...
                    </SelectItem>
                  ) : authors && authors.length > 0 ? (
                    authors.map((author) => (
                      <SelectItem key={author.id} value={author.id}>
                        {author.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>
                      No authors available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <BookFormatForm formats={formats} onFormatsChange={setFormats} />

        <div className="flex justify-end space-x-4">
          <Button 
            type="button" 
            variant="outline"
            onClick={() => {
              form.reset();
              setFormats([{
                format: 'paperback',
                isbn: '',
                publishDate: format(new Date(), 'yyyy-MM-dd')
              }]);
            }}
            disabled={createBook.isPending}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={createBook.isPending}
          >
            {createBook.isPending ? 'Adding...' : 'Add Book'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddBookForm;
